import { addDays, addHours, differenceInHours, differenceInMinutes, startOfHour } from 'date-fns';
import { format, utcToZonedTime } from 'date-fns-tz';
import Highcharts, { AxisSetExtremesEventObject, Options as HighchartsOptions, XAxisOptions } from 'highcharts';
import highchartsGantt from 'highcharts/modules/gantt';
import HighchartsReact from 'highcharts-react-official';
import debounce from 'lodash.debounce';
import { clone } from 'ramda';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PerfectScrollbar from 'react-perfect-scrollbar';
import ReactResizeDetector from 'react-resize-detector';

import { TRANSITION_TIME_DELAY, ganttChartOptions, ganttChartSeriesConfig } from './config';
import { GanttChartProps } from './types';
import { formatScheduleElementLabel, ganttChartTooltipFormatter, mapGanttChartData } from './utils';
import { useAuth } from '../../app/AuthorizationProvider';
import { FullSizeMessage } from '../../components/FullSizeMessage';
import { STATIC_ELEMENT_HEIGHT, STATIC_POINT_HEIGHT } from '../../constants';
import { getZonedDateTimezoneOffset } from '../../utils/date';
import { MIN_15, MIN_1, MIN_5, ONE_HOUR } from '../Scheduling/config';

highchartsGantt(Highcharts);

export const CHART_15_MIN_INTERVAL_WIDTH = 40;

export const GanttChartComponent: React.FC<GanttChartProps> = ({
  data,
  isDataEmpty,
  isLoading,
  pickedDate,
  scheduleElementsData,
  timezone,
}) => {
  const { localeObj } = useAuth();

  const scrollbarRef = useRef<PerfectScrollbar>(null);
  const chartRef = useRef<HighchartsReact>(null);

  const [containerRef, setContainerRef] = useState<HTMLElement | null>(null);
  const [scrollbarContainerWidth, setScrollbarContainerWidth] = useState(0);

  const { t } = useTranslation();

  /**
   * Issue https://github.com/highcharts/highcharts/issues/6943
   * To prevent new setExtremes() and afterSetExtremes() handlers from being added to existing ones,
   * when using native Highcharts' update function (they should have been replaced instead).
   * final fix will be to upgrade Highcharts to the newest version once defined in issue 6943
   */
  useEffect(() => {
    const xAxis = chartRef?.current?.chart.xAxis[0];
    if (xAxis) {
      Highcharts.removeEvent(xAxis, 'setExtremes');
      Highcharts.removeEvent(xAxis, 'afterSetExtremes');
    }
  }, [pickedDate]);

  useEffect(() => {
    if (containerRef) {
      containerRef.setAttribute('style', 'scroll-behavior: smooth;');
      containerRef.scrollLeft = 0;
      containerRef.removeAttribute('style');
    }
    chartRef.current?.chart.zoomOut();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const endDateTime = addDays(pickedDate, 1);
  const endDateTimestamp = endDateTime.getTime();

  const {
    data: chartData,
    maxX,
    minX,
  } = useMemo(
    () => mapGanttChartData(pickedDate, endDateTimestamp, timezone, t, data, scheduleElementsData),
    [data, endDateTimestamp, pickedDate, scheduleElementsData, timezone, t]
  );

  const constrainedMaxX = maxX ?? endDateTime;
  const constrainedMinX = minX ?? new Date(pickedDate);

  const maxXInMilliseconds = constrainedMaxX.getTime();
  const startOfHourMaxXInMilliseconds = startOfHour(constrainedMaxX).getTime();

  const timelineStartDateTime = startOfHour(constrainedMinX).getTime();
  const timelineEndDateTime =
    startOfHourMaxXInMilliseconds === maxXInMilliseconds
      ? maxXInMilliseconds
      : addHours(startOfHourMaxXInMilliseconds, 1).getTime();

  const timelineHoursCount = differenceInHours(timelineEndDateTime, timelineStartDateTime);
  const timelineWidth = timelineHoursCount * 4 * CHART_15_MIN_INTERVAL_WIDTH;
  const chartWidth = Math.max(
    timelineWidth,
    containerRef?.getBoundingClientRect().width ?? scrollbarContainerWidth - 50
  );

  const options: HighchartsOptions = {
    ...ganttChartOptions,
    chart: {
      ...ganttChartOptions.chart,
      width: chartWidth,
      renderTo: 'div',
    },
    series: [
      {
        data: [
          {
            start: timelineStartDateTime,
            end: timelineEndDateTime,
            color: '#f7f8f9',
            y: 0,
          },
        ],
        enableMouseTracking: false,
        id: 'out-of-adherence-plot-band',
        // @ts-expect-error: incompatible type 😥
        pointWidth: STATIC_POINT_HEIGHT + 4,
        type: 'gantt',
        xAxis: 0,
      },
      ...ganttChartSeriesConfig.map(({ id, nameKey }) => ({
        id,
        cropThreshold: 1,
        data: clone(chartData[id]),
        dataLabels:
          id === 'outOfAdherence'
            ? {
                enabled: false,
              }
            : {
                defer: false,
                enabled: true,
                formatter: formatScheduleElementLabel(id === 'scheduled'),
                style: {
                  fontSize: '12px',
                },
              },
        name: t(nameKey),
        type: 'gantt' as const,
        xAxis: 0,
      })),
    ],
    time: {
      getTimezoneOffset: (timestamp: number) =>
        getZonedDateTimezoneOffset(utcToZonedTime(timestamp, timezone), timezone),
      useUTC: true,
    },
    tooltip: {
      ...ganttChartOptions.tooltip,
      formatter: ganttChartTooltipFormatter(t, timezone, 'adherence-history'),
    },
    xAxis: [
      {
        ...(ganttChartOptions.xAxis as XAxisOptions[])[0],
        events: {
          setExtremes(event: AxisSetExtremesEventObject) {
            /**
             * should take care of zooming-out functionality only, because for some reason,
             * zooming-in on this layer is causing performance issues
             */

            if (!containerRef || !event.trigger || !chartRef?.current) {
              return event;
            }

            const { min: currentMin, max: currentMax } = event;
            const isZoomed =
              (currentMin || currentMax) &&
              (timelineStartDateTime !== currentMin || timelineEndDateTime !== currentMax);

            if (isZoomed) {
              // when zoomed-in, update of axes should be in responsibility of afterSetExtremes handler
              return event;
            }

            if ((chartRef?.current?.chart.options.xAxis as XAxisOptions[])[0].tickInterval !== MIN_15) {
              chartRef?.current?.chart.xAxis[0].update({
                tickInterval: MIN_15,
              });
              chartRef?.current?.chart.xAxis[1].update({
                tickInterval: ONE_HOUR,
              });
            }

            if (chartRef?.current?.chart.chartWidth !== chartWidth) {
              chartRef?.current?.chart.setSize(chartWidth);
              scrollbarRef?.current?.forceUpdate();
            }

            return event;
          },
          afterSetExtremes(event: AxisSetExtremesEventObject) {
            /**
             * should take care of zooming-in functionality only, because for some reason,
             * zooming-out on this layer is causing performance issues
             */

            if (!containerRef || !event.trigger || !chartRef?.current) {
              return;
            }

            const { min: currentMin, max: currentMax } = event;
            const isZoomed =
              (currentMin || currentMax) &&
              (timelineStartDateTime !== currentMin || timelineEndDateTime !== currentMax);

            let tickInterval: number;
            const zoomedRangeInMinutes = differenceInMinutes(currentMax, currentMin);

            if (zoomedRangeInMinutes <= 15) {
              // use 1 minute tick interval when zoom range is 15 minutes or less
              tickInterval = MIN_1;
            } else if (zoomedRangeInMinutes <= 60) {
              // use 5 minutes tick interval when zoom range is between 15 minutes and 1 hour
              tickInterval = MIN_5;
            } else {
              // use 15 minutes tick interval by default
              tickInterval = MIN_15;
            }

            if (!isZoomed) {
              // when zoomed-out, update of axes should be in responsibility of setExtremes handler
              return;
            }

            if ((chartRef?.current?.chart.options.xAxis as XAxisOptions[])[0].tickInterval !== tickInterval) {
              chartRef?.current?.chart.xAxis[0].update({
                tickInterval,
              });
            }

            if (
              zoomedRangeInMinutes < 60 &&
              (chartRef?.current?.chart.options.xAxis as XAxisOptions[])[1].tickInterval !== MIN_15
            ) {
              chartRef?.current?.chart.xAxis[1].update({
                tickInterval: MIN_15,
              });
            }

            const outerContainerWidth = containerRef.getBoundingClientRect().width;
            const newWidth = outerContainerWidth ? Math.round(outerContainerWidth) : null;

            if (chartRef?.current?.chart.chartWidth !== newWidth) {
              chartRef?.current?.chart.setSize(newWidth);
              scrollbarRef?.current?.forceUpdate();
            }
          },
        },
        labels: {
          ...(ganttChartOptions.xAxis as XAxisOptions[])[0].labels,
          formatter() {
            // eslint-disable-next-line react/no-this-in-sfc
            const dateTime = this.value;
            const zonedDateTime = utcToZonedTime(dateTime, timezone);
            const hours = format(zonedDateTime, 'HH', { locale: localeObj, timeZone: timezone });
            const minutes = format(zonedDateTime, 'mm', { locale: localeObj, timeZone: timezone });

            return `<span class="schedule-time">${hours}:${minutes}</span>:${minutes}`;
          },
        },
        max: timelineEndDateTime,
        min: timelineStartDateTime,
      },
      {
        ...(ganttChartOptions.xAxis as XAxisOptions[])[1],
        labels: {
          ...(ganttChartOptions.xAxis as XAxisOptions[])[1].labels,
          formatter() {
            // eslint-disable-next-line react/no-this-in-sfc
            const dateTime = this.value;

            return format(utcToZonedTime(dateTime, timezone), t('date.formats.timeShort'), {
              locale: localeObj,
              timeZone: timezone,
            });
          },
        },
        max: timelineEndDateTime,
        min: timelineStartDateTime,
      },
    ],
  };

  useEffect(() => {
    const chart = chartRef.current?.chart;

    if (!chart) {
      return;
    }

    const top = chart.plotTop;

    // set background color for xAxis minutes section (adding new rectangle underneath the axis elements)
    chart.renderer
      .rect(0, top - STATIC_ELEMENT_HEIGHT, chartWidth, STATIC_ELEMENT_HEIGHT - 2)
      .add()
      .attr({ fill: '#f7f8f9' });

    // set background color for xAxis hours section (adding new rectangle underneath the axis elements)
    chart.renderer
      .rect(0, top - 2 * STATIC_ELEMENT_HEIGHT - 8, chartWidth, STATIC_ELEMENT_HEIGHT + 6)
      .add()
      .attr({ fill: '#d4dee3' });
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateChartSize = useCallback(
    debounce(() => {
      chartRef?.current?.chart.zoomOut();
      scrollbarRef.current?.forceUpdate();
      chartRef?.current?.chart.reflow();
    }, TRANSITION_TIME_DELAY),
    [scrollbarRef, chartRef]
  );

  const onResizeHandler = (width?: number) => {
    if (width === undefined) {
      return;
    }

    setScrollbarContainerWidth(Math.round(width));
    chartRef?.current?.chart.reflow();
    updateChartSize();
  };

  return (
    <ReactResizeDetector handleWidth onResize={onResizeHandler} refreshMode="debounce" refreshRate={10}>
      <PerfectScrollbar
        className="adherence-history__gantt-chart-scrollbar-container"
        containerRef={setContainerRef}
        ref={scrollbarRef}
        options={{ scrollingThreshold: 100, swipeEasing: true, scrollXMarginOffset: 10 }}
      >
        <div data-test-id="adherence-history-detail:gantt-chart">
          {isLoading && (!data || !scheduleElementsData) ? null : (
            <>
              {isDataEmpty ? (
                <FullSizeMessage headline={t('filter.noDataAvailable')} iconKind="no_data" />
              ) : (
                <HighchartsReact
                  key={pickedDate}
                  ref={chartRef}
                  highcharts={Highcharts}
                  options={options}
                  constructorType="ganttChart"
                />
              )}
            </>
          )}
        </div>
      </PerfectScrollbar>
    </ReactResizeDetector>
  );
};

export const GanttChart = memo(GanttChartComponent);
